/**
 * Shared time-block utilities (frontend port of order_blocks_chronologically).
 *
 * The algorithm matches the Python helper in backend/services/payroll_calc.py:
 *   1. Project blocks onto the 24-hour circle.
 *   2. Find the longest uncovered gap — the shift starts where it ends.
 *   3. Compute offset_start / offset_end for each block and sort.
 *
 * Each returned block carries its original index as `_orig_idx`.
 */

function toMin(t) {
  if (!t) return 0;
  const parts = t.split(':');
  return parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10);
}

/**
 * Order blocks in true chronological (shift) order, handling overnight shifts.
 *
 * Pure function — returns a *new* array of *new* objects with
 * `_offset_start`, `_offset_end` and `_orig_idx` added.
 *
 * For a normal day shift the result is identical to sorting by start_time.
 *
 * @param {Array} blocks – each must have `start_time` and `end_time` as "HH:MM".
 * @returns {Array}
 */
export function orderBlocksChronologically(blocks) {
  if (!blocks || blocks.length === 0) return [];

  // Build items: { s, e, dur, origIdx }
  const items = blocks.map((b, i) => {
    const s = toMin(b.start_time);
    const e = toMin(b.end_time);
    let dur;
    if (e > s) dur = e - s;
    else if (e < s) dur = 1440 - s + e; // crosses midnight
    else dur = 0;
    return { s, e, dur, origIdx: i };
  });

  // --- Find shift_start via the largest uncovered gap ---

  // Project onto linear segments on [0, 1440)
  const segments = [];
  for (const { s, e, dur } of items) {
    if (dur === 0) continue;
    if (e <= s) {
      // crosses midnight
      if (s < 1440) segments.push([s, 1440]);
      if (e > 0) segments.push([0, e]);
    } else {
      segments.push([s, e]);
    }
  }

  let shiftStart;
  if (segments.length === 0) {
    shiftStart = Math.min(...items.map((x) => x.s));
  } else {
    // Sort and merge
    segments.sort((a, b) => a[0] - b[0]);
    const merged = [[...segments[0]]];
    for (let k = 1; k < segments.length; k++) {
      const [segS, segE] = segments[k];
      const last = merged[merged.length - 1];
      if (segS <= last[1]) {
        last[1] = Math.max(last[1], segE);
      } else {
        merged.push([segS, segE]);
      }
    }

    // Walk gaps (including wraparound last→first)
    let bestGap = -1;
    let bestGapEnd = merged[0][0];
    for (let idx = 0; idx < merged.length; idx++) {
      const nxt = (idx + 1) % merged.length;
      const gapStart = merged[idx][1];
      const gapEnd = merged[nxt][0];
      const gapSize = nxt === 0
        ? ((gapEnd + 1440 - gapStart) % 1440)
        : (gapEnd - gapStart);
      if (gapSize > bestGap) {
        bestGap = gapSize;
        bestGapEnd = merged[nxt][0];
      }
    }
    shiftStart = bestGap > 0 ? bestGapEnd : Math.min(...items.map((x) => x.s));
  }

  // Compute offsets and sort
  const withOffsets = items.map(({ s, dur, origIdx }) => {
    const offsetStart = ((s - shiftStart) % 1440 + 1440) % 1440;
    const offsetEnd = offsetStart + dur;
    return { offsetStart, offsetEnd, origIdx };
  });

  withOffsets.sort((a, b) => a.offsetStart - b.offsetStart || a.offsetEnd - b.offsetEnd);

  return withOffsets.map(({ offsetStart, offsetEnd, origIdx }) => ({
    ...blocks[origIdx],
    _offset_start: offsetStart,
    _offset_end: offsetEnd,
    _orig_idx: origIdx,
  }));
}
